// Embed / HTML / Shortcode injector.
//
// data-embed wrappers are emitted server-side by embed.php, html.php and
// shortcode.php. Only the variant (desktop or custom phone layout) that
// matches the current screen size gets hydrated, so duplicate script IDs
// from a user pasting the same code into both layouts can't collide.
//
// On screen-size change we don't re-hydrate the other variant (which
// re-runs embed init and breaks for one-shot scripts like ThriveCart) —
// instead we pair desktop and CPL twins via data-embed-fp (md5 of the
// content), then physically move the live DOM children from the old
// wrapper into the now-active wrapper. The embed runs exactly once;
// state is preserved.
//
// Side issue: hydration happens after DOMContentLoaded / window.load have
// already fired, so embedded snippets that rely on those events would
// otherwise silently no-op. For the duration of the inject we shim
// document.addEventListener('DOMContentLoaded', ...) and
// window.addEventListener('load', ...) to fire the listener immediately
// when the document is past that milestone. The patch is reverted right
// after the synchronous inject, so external async scripts pulled in by
// the embed are not affected — modern third-party embeds (ThriveCart,
// Calendly, Stripe, Typeform, …) don't depend on DCL anyway.

const HYDRATED_ATTR = 'data-embed-hydrated'
const FP_ATTR = 'data-embed-fp'

// Verbose logging is off by default — production stays quiet. Turn it on
// when you need to follow hydrate / swap decisions during page load and
// resize, by either:
//   • appending `?lay-debug` (or `&lay-debug`) to the URL,
//   • running `window.__layEmbedInjectDebug = true` once in the console,
//   • or setting `localStorage.setItem('lay-debug', '1')` (sticky across
//     reloads).
// Then filter the dev console for `[embed-inject]`.
const isDebugEnabled = () => {
	if (typeof window === 'undefined') return false
	if (window.__layEmbedInjectDebug === true) return true
	try {
		if (window.location && window.location.search && window.location.search.indexOf('lay-debug') !== -1) return true
		if (window.localStorage && window.localStorage.getItem('lay-debug') === '1') return true
	} catch (e) {}
	return false
}

const log = function () {
	if (!isDebugEnabled()) return
	if (!window.console || !console.info) return
	const args = ['[embed-inject]']
	for (let i = 0; i < arguments.length; i++) args.push(arguments[i])
	console.info.apply(console, args)
}

const describe = (el) => {
	if (!el) return 'null'
	const fp = el.getAttribute(FP_ATTR) || ''
	const hyd = el.getAttribute(HYDRATED_ATTR) || ''
	const grid = el.closest ? el.closest('#grid, #custom-phone-grid, #footer, #footer-custom-phone-grid') : null
	const where = grid ? ('#' + grid.id) : '?'
	const cls = (el.className || '').toString().split(/\s+/)[0] || el.tagName
	return where + ' ' + cls + ' fp=' + fp.slice(0, 8) + ' hyd=' + (hyd || '-')
}

const isDocReady = () => document && document.readyState !== 'loading'
const isWindowLoaded = () => document && document.readyState === 'complete'

const fireSoon = (listener, eventName) => {
	setTimeout(() => {
		try {
			const fn = typeof listener === 'function' ? listener : listener && listener.handleEvent
			if (typeof fn === 'function') {
				fn.call(listener === fn ? document : listener, new Event(eventName))
			}
		} catch (e) {
			if (window.console && console.error) console.error(e)
		}
	}, 0)
}

const installReadyShim = () => {
	const docTarget = document
	const winTarget = window
	const origDocAdd = docTarget.addEventListener
	const origWinAdd = winTarget.addEventListener

	docTarget.addEventListener = function (type, listener) {
		if (type === 'DOMContentLoaded' && isDocReady() && listener) {
			fireSoon(listener, 'DOMContentLoaded')
			return
		}
		return origDocAdd.apply(docTarget, arguments)
	}
	winTarget.addEventListener = function (type, listener) {
		if (type === 'load' && isWindowLoaded() && listener) {
			fireSoon(listener, 'load')
			return
		}
		return origWinAdd.apply(winTarget, arguments)
	}

	return () => {
		docTarget.addEventListener = origDocAdd
		winTarget.addEventListener = origWinAdd
	}
}

const hydrateFresh = (wrapper) => {
	const html = wrapper.getAttribute('data-embed')
	if (html == null) return
	log('hydrateFresh', describe(wrapper), 'len=' + html.length)
	const restore = installReadyShim()
	try {
		jQuery(wrapper).html(html)
	} finally {
		restore()
	}
	wrapper.setAttribute(HYDRATED_ATTR, '1')
}

// Element is in a tree that's actually rendered (not display:none and not
// detached). offsetParent === null means a position:static ancestor has
// display:none, which is exactly how the inactive grid is hidden via the
// .hascustomphonegrid > #grid { display:none } rule.
//
// Caveat: `.lay-html` wrappers use `display: contents` (so user HTML stays
// layout-transparent), which means they generate no box at all — their
// offsetParent is null and getClientRects() is empty even when "visible".
// We fall back to walking the parent chain past `display: contents`
// ancestors and probe the first real-box ancestor instead.
const isVisuallyMounted = (el) => {
	if (!el) return false
	if (el.offsetParent !== null) return true
	if (el.getClientRects().length > 0) return true
	let p = el.parentElement
	while (p) {
		const cs = window.getComputedStyle ? window.getComputedStyle(p) : null
		const display = cs ? cs.display : ''
		if (display === 'none') return false
		if (display !== 'contents') {
			if (p.offsetParent !== null) return true
			if (p.getClientRects().length > 0) return true
			return false
		}
		p = p.parentElement
	}
	return false
}

const moveChildren = (from, to) => {
	while (from.firstChild) {
		to.appendChild(from.firstChild)
	}
}

// Pair key = the col's relid (so desktop and CPL twins of the same
// element pair up, but identical content in different cols does not).
// Combined with the fp so multiple embeds inside a single col stack
// stay distinct from each other.
const getColRelid = (el) => {
	const col = el.closest ? el.closest('.col') : null
	if (!col || !col.classList) return null
	for (let i = 0; i < col.classList.length; i++) {
		const cls = col.classList[i]
		if (cls.indexOf('id-') === 0) return cls.substring(3)
	}
	return null
}

const getPairKey = (el) => {
	const fp = el.getAttribute(FP_ATTR)
	if (!fp) return null
	const relid = getColRelid(el)
	return relid ? relid + ':' + fp : 'nofp:' + fp
}

// Walk all data-embed wrappers in scope and make sure exactly one twin
// per pair group is hydrated — the one that's currently visible.
// If a twin is already hydrated, transplant; otherwise hydrate fresh.
const rebalanceEmbedsInScope = (scope) => {
	if (!scope || typeof scope.querySelectorAll !== 'function') return
	const wrappers = Array.from(scope.querySelectorAll('[data-embed]'))
	if (!wrappers.length) {
		log('rebalance: no wrappers in scope', scope && scope.id ? '#' + scope.id : scope)
		return
	}

	log('rebalance: scope', scope && scope.id ? '#' + scope.id : (scope.className || scope.tagName), '| wrappers:', wrappers.length)
	wrappers.forEach((el) => {
		log('  wrapper', describe(el), 'mounted=' + isVisuallyMounted(el))
	})

	const groups = new Map()
	const orphans = []
	wrappers.forEach((el) => {
		const key = getPairKey(el)
		if (!key) {
			orphans.push(el)
			return
		}
		if (!groups.has(key)) groups.set(key, [])
		groups.get(key).push(el)
	})

	log('  groups:', groups.size, '| orphans:', orphans.length)

	groups.forEach((group, key) => {
		if (group.length === 1) {
			// No twin — hydrate it if visible and not yet hydrated.
			const only = group[0]
			if (only.getAttribute(HYDRATED_ATTR) === '1') {
				log('  group', key, 'singleton already hydrated — skip')
				return
			}
			if (!isVisuallyMounted(only)) {
				log('  group', key, 'singleton not mounted — defer')
				return
			}
			log('  group', key, 'singleton — hydrate fresh')
			hydrateFresh(only)
			return
		}

		const active = group.find(isVisuallyMounted)
		if (!active) {
			log('  group', key, 'pair but nothing visible — defer')
			return
		}
		if (active.getAttribute(HYDRATED_ATTR) === '1') {
			log('  group', key, 'active already hydrated — skip')
			return
		}

		const hydrated = group.find((el) => el.getAttribute(HYDRATED_ATTR) === '1')
		if (hydrated) {
			log('  group', key, 'transplant from', describe(hydrated), 'to', describe(active))
			moveChildren(hydrated, active)
			hydrated.removeAttribute(HYDRATED_ATTR)
			active.setAttribute(HYDRATED_ATTR, '1')
		} else {
			log('  group', key, 'pair — hydrate active fresh', describe(active))
			hydrateFresh(active)
		}
	})

	orphans.forEach((el) => {
		if (el.getAttribute(HYDRATED_ATTR) === '1') return
		if (!isVisuallyMounted(el)) return
		log('  orphan — hydrate fresh', describe(el))
		hydrateFresh(el)
	})
}

// Hook called from load_cpl_or_desktop_content_controller after a target
// region has had its image / video / iframe loaders kicked off. The
// region passed in is just one grid (e.g. #grid or #custom-phone-grid),
// but pair scope has to be the whole .lay-content so twin wrappers in
// the sibling grid are seen — otherwise we'd treat each side as a
// standalone embed and end up hydrating both in parallel.
const injectAllDataEmbedsIn = ($target) => {
	if (!$target) return
	log('injectAllDataEmbedsIn — $target length:', $target.length)
	const seen = new Set()
	$target.each(function () {
		const root = this && this.closest ? this.closest('.lay-content') : null
		const scope = root || this
		if (!scope || seen.has(scope)) return
		seen.add(scope)
		rebalanceEmbedsInScope(scope)
	})
}

// Re-balance on every screen-size change. The CPL controller's per-region
// `loaded` flags don't fire loadContent twice for the same grid, so the
// transplant has to be wired here independently.
const bindRebalanceOnSizeChange = () => {
	if (!window.laytheme || typeof window.laytheme.on !== 'function') return
	window.laytheme.on('sizechanged', (size) => {
		log('sizechanged →', size, '— rebalancing all .lay-content roots')
		document.querySelectorAll('.lay-content').forEach((root) => {
			rebalanceEmbedsInScope(root)
		})
	})
}

// Re-balance once on init too, in case a page started up phone-sized,
// hydrated CPL, then is now flipping back to desktop without a sizechange
// (rare but happens via SPA flows / overlay open).
const init = () => {
	bindRebalanceOnSizeChange()
}

export { injectAllDataEmbedsIn, rebalanceEmbedsInScope, init }
